/**
 * useChatBulkActions — Iter 156.22 (Phase A, Yash).
 * Extracted from `ChatsPage.jsx`. Owns multi-select bulk-action state for
 * the chat list: bulk-mode toggle, per-chat selection set, the menu
 * visibility flag, and the three handlers (toggle one, select-all-visible,
 * execute a server-side bulk action).
 *
 * Caller passes `chats` (current visible list) for the select-all helper
 * and `refetchChats` so we can refresh after a successful bulk mutation.
 */
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { bulkActionsApi } from "../lib/api";

export default function useChatBulkActions(chats, refetchChats) {
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkMenu, setShowBulkMenu] = useState(false);

  const toggleBulkSelect = useCallback((chatId) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(chatId)) n.delete(chatId);
      else n.add(chatId);
      return n;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === chats.length) return new Set();
      return new Set(chats.map((c) => c.id));
    });
  }, [chats]);

  const executeBulkAction = useCallback(async (action, value = "") => {
    if (selectedIds.size === 0) return;
    try {
      const res = await bulkActionsApi.execute([...selectedIds], action, value);
      toast.success(res.data.message);
      setSelectedIds(new Set());
      setBulkMode(false);
      setShowBulkMenu(false);
      if (typeof refetchChats === "function") refetchChats(1);
    } catch {
      toast.error("Bulk action failed");
    }
  }, [selectedIds, refetchChats]);

  return {
    bulkMode, setBulkMode,
    selectedIds, setSelectedIds,
    showBulkMenu, setShowBulkMenu,
    toggleBulkSelect, selectAllVisible, executeBulkAction,
  };
}
